import { useCallback, useEffect, useRef, useState } from 'react';
import type { LatencyHistoryHealth } from '../lib/api.js';
import { fetchLatencyHealth } from '../lib/api.js';
import {
  type DashboardResource,
  createDashboardResource,
  markResourceError,
  markResourceLoading,
  markResourceSuccess,
  updateResourceStaleness,
} from '../lib/dashboardResource.js';

interface UseLatencyHealthOptions {
  region?: string;
  hours?: number;
  pollIntervalMs?: number;
  staleAfterMs?: number;
}

interface UseLatencyHealthResult {
  health: DashboardResource<LatencyHistoryHealth>;
  refreshHealth: () => Promise<void>;
}

export function useLatencyHealth({
  region,
  hours = 24,
  pollIntervalMs = 60_000,
  staleAfterMs = 90_000,
}: UseLatencyHealthOptions = {}): UseLatencyHealthResult {
  const [health, setHealth] = useState(createDashboardResource<LatencyHistoryHealth>());
  const mountedRef = useRef(true);

  const refreshHealth = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    setHealth((resource) => markResourceLoading(resource));
    try {
      const payload = await fetchLatencyHealth({ region, hours });
      if (!mountedRef.current) {
        return;
      }
      setHealth((resource) => markResourceSuccess(resource, payload, new Date().toISOString()));
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      setHealth((resource) => markResourceError(resource, error));
    }
  }, [region, hours]);

  useEffect(() => {
    mountedRef.current = true;
    refreshHealth();
    const poller = setInterval(() => {
      refreshHealth();
    }, pollIntervalMs);
    const staleness = setInterval(() => {
      setHealth((resource) => updateResourceStaleness(resource, staleAfterMs));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      clearInterval(poller);
      clearInterval(staleness);
    };
  }, [hours, pollIntervalMs, refreshHealth, staleAfterMs]);

  return { health, refreshHealth };
}
